<template>
  <el-card
    id="myJourneyCard"
    v-loading="!previewMode && user.loading"
    shadow="hover"
    class="clear-card myJourney"
  >
    <!-- Mobile journey Title -->
    <div
      v-if="$mq === 'mobile' || $mq === 'tablet'"
      class="titleMobile"
    >
      <b>{{ $t('session.sessionCap') }}</b>
    </div>

    <!-- Desk journey Title -->
    <div
      v-else
      class="title defaultCursor"
    >
      <b>{{ $t('welcome.myJourney') }}</b>
    </div>

    <!-- Mobile journey -->
    <div
      v-if="$mq === 'mobile' || $mq === 'tablet'"
      class="contentMobile"
    >
      <div class="session">
        {{ activeSteps === 0 ? 'CS' : activeSteps }}
      </div>
    </div>

    <!-- Desk journey -->
    <div
      v-else
      id="stepScrollWrapper"
      class="content"
    >
      <el-steps
        :active="activeSteps"
        align-center
        :style="{
          minWidth: formattedSessionWrapWidth ? `${formattedSessionWrapWidth}px` : null,
        }"
      >
        <el-step
          v-for="(step, SKey) in formattedSession"
          :id="activeSteps - 1 === SKey ? 'shortLine' : 'longLine' + SKey"
          :key="`Step${SKey}`"
          :title="SKey === 0 ? $t('session.coachSelection') : `${$t('session.sessionCap')} ${SKey}`"
          :class="{
            currentSession: activeSteps === SKey,
            completed: activeSteps === SKey && status.meeting_status === 'completed',
            cancelled: !step.status,
            expected: assumingStep === SKey,
          }"
        >
          <template slot="icon">
            <el-tooltip
              class="tooltip"
              effect="dark"
              :content="!step.status ? 'Canceled session'
                : (currentSession && step.session === currentSession.split('_')[1] * 1)
                  ? 'Current session'
                  : step.session === 'CS'
                    ? $t('session.coachSelection') : `${$t('session.sessionCap')} ${step.session}`"
              placement="bottom"
            >
              <div class="defaultCursor">
                {{ step.session }}
              </div>
            </el-tooltip>
          </template>
        </el-step>
      </el-steps>
    </div>
  </el-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  props: {
    previewMode: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      status: (state) => state.processStatus.process,
    }),

    ...mapGetters('processStatus', {
      currentSession: 'currentSession',
    }),

    formattedSession() {
      let list = [];
      if (this.status && this.status.duration) {
        list = [...Array(this.status.duration + 1).keys()];
      } else {
        list = [...Array(5).keys()];
      }

      const steps = list.map((el) => {
        if (el === 0) {
          return { session: 'CS', status: true };
        }

        let status = false;
        if (this.status && this.status.session_list) {
          status = this.status.session_list.some((sl) => sl === `session_${el}`);
        }
        return {
          session: el,
          status,
        };
      });

      return steps;
    },

    activeSteps() {
      // Step position setter
      let step;

      try {
        if (this.previewMode) {
          step = 1;
        } else if (this.currentSession === 'on_boarding') {
          step = 0;
        } else {
          step = this.currentSession.split('_')[1] * 1;
        }
      } catch {
        step = 0;
      }

      return step;
    },

    assumingStep() {
      let step;

      if (this.currentSession && this.currentSession.split('_')[0] !== 'on' && this.status) {
        if (this.currentSession.split('_')[1] * 1 >= 4) {
          if (this.status.meeting_status === 'completed' && this.status.next_meeting) {
            step = this.currentSession.split('_')[1] * 1 + 1;
          }
        }
      }

      return step;
    },

    formattedSessionWrapWidth() {
      let width = null;
      if (this.formattedSession && this.formattedSession.length) {
        switch (this.formattedSession.length) {
          case 11:
            width = 1300;
            break;
          case 14:
            width = 1650;
            break;
          default:
            break;
        }
      }
      return width;
    },
  },

  watch: {
    activeSteps: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.setStepPosition();
        }
      },
    },
  },

  methods: {
    setStepPosition() {
      setTimeout(() => {
        if (document.getElementsByClassName('currentSession')[0]) {
          document.getElementsByClassName('currentSession')[0].scrollIntoView({
            behavior: 'smooth', block: 'center', inline: 'center',
          });
        }
      }, 400);
    },
  },
};
</script>

<style lang="scss" scoped>
  #myJourneyCard {
    height: 100%;

    .titleMobile {
      text-align: center;
    }

    .title {
      text-align: left;
    }

    .contentMobile {
      text-align: center;
      margin-top: 20px;

      .session {
        margin: 0 auto;
        font-weight: bold;
        font-size: 24px;
        border-radius: 50%;
        border: 4px solid #ffb800;
        height: 46px;
        width: 46px;
        line-height: 46px;
      }
    }
  }
</style>

<style lang="scss">
  #myJourneyCard {
    .content {
      padding: 30px 0 10px;
      min-height: 100px;
      box-sizing: border-box;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-color: #c8c8c8 transparent;
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: #c8c8c8;
        border-radius: 8px;
      }

      &::-webkit-scrollbar-track {
        background: transparent !important;
      }

      .el-step {
        width: 10%;

        .tooltip {
          cursor: default;
          display: flex;
          width: 100%;
          height: 100%;
          align-items: center;
          justify-content: center;
        }

        .el-step__title {
          color: var(--themeFontColor) !important;
          line-height: normal;
          margin-top: 10px;
        }

        .el-step__title.is-finish,
        .el-step__title.is-wait {
          display: none;
        }

        .el-step__icon {
          width: 40px;
          height: 40px;
          border-width: 2px;
          background-color: transparent;
          font-weight: bold;
          color: #c4c4c4;
        }

        // Global step head, line
        .el-step__head {
          .el-step__line {
            top: 19px;
            height: 2px;
            left: calc(50% + 24px);
            right: calc(-50% + 24px);
            border-radius: 10px;

            .el-step__line-inner {
              border-width: 1px !important;
              border-radius: 10px;
            }
          }
        }

        // Finished step head, line
        .is-finish {
          border-color: #66d73f;
          color: #66d73f;

          .el-step__icon {
            background-color: #66d73f;
            color: white;
          }

          .el-step__line {
            top: 18px;
            height: 0;
            left: 50%;
            right: calc(-50% + 17px);
            border-radius: 10px;

            .el-step__line-inner {
              width: 100% !important;
              border-width: 2px !important;
              border-radius: 10px;
            }
          }
        }

        // Process step head, line
        .el-step__head.is-process {
          border-color: #ffb800;
          color: #66d73f;

          .el-step__icon {
            background-color: transparent;
            border-width: 4px;
            color: var(--themeFontColor);
          }

          .el-step__line {
            .el-step__line-inner {
              border-width: 0 !important;
              border-radius: 10px;
            }
          }
        }

        &.cancelled {
          .el-step__head {
            .el-step__icon {
              opacity: 0.5;
            }

            .el-step__line {
              left: calc(50% + 24px);
              right: calc(-50% + 24px);
            }
          }
        }

        &.expected {
          .el-step__head {
            border-color: #ffb800;
            color: #66d73f;

            .el-step__icon {
              background-color: transparent;
              border-width: 4px;
              color: var(--themeFontColor);
            }
          }
        }

        // Fill process step background when meeting is completed
        &.currentSession.completed {
          .el-step__head.is-process {
            .el-step__icon {
              border-color: #66d73f;
              background-color: #66d73f;
            }
          }
        }
      }

      #shortLine {
        .is-finish {
          .el-step__line {
            right: calc(-50% + 25px) !important;
          }
        }
      }
    }
  }
</style>
